/**
 * WCC360 v2 — Convergence of Passions & Careers.
 *
 * The learner's interests, drawn as overlapping circles, with the roles that
 * sit inside more than one of them called out as the convergence. It is a
 * *presentation* of the engine's own output — nothing here scores, filters or
 * re-weights anything:
 *
 *   - Circle membership for a stated-interest circle is career FAMILY
 *     membership, exactly the families that passion / cause / hobby / dream /
 *     pull / background already contributed as affinity.
 *   - Circle membership for a Part 2 signal circle is the per-role FIT score
 *     the engine already computed for that signal (`domain`, `valuesDetail`,
 *     `contextDetail`). We show the roles that fit; we never invent a score.
 *   - Only eligible, already-matched roles can appear. Eligibility is decided
 *     once, upstream, by `matchCareers`. A circle can never resurrect a role
 *     the gate ruled out, and can never delete one it let through.
 *
 * Every circle carries its own audit trail (`inputs`): the item code, the
 * screen, the question, and what the learner actually answered. That is the
 * whole point — a diagram nobody can check is decoration.
 *
 * CANON-PENDING: the convergence view is a presentation artefact of the v2
 * client preview engine. It introduces no new psychometric construct.
 */
import type { ImpactDomain, Learner, MatchResult } from "@/lib/scoring";
import type { Part1Answers } from "./types";
import { emptySlots, slotValue, type SlotKey, type SlotState } from "./slots";
import { selectedPassions } from "./passions";
import { PASSIONS, type Passion } from "@/data/passions";
import {
  HOBBY_FREQUENCY_PHRASE, ORDINAL, causeById, isCauseId, isHobbyId,
} from "./causes";
import { dreamById, selectedPulls } from "./careerPull";
import { fieldById } from "./background";
import { DEFAULT_BAND, type Band } from "./band";
import {
  OBSERVER_ITEM_WEIGHT, OBSERVER_TOTAL_CAP, observerFamilyCounts, relationshipNoun,
  type ApprovedObserverInput, type ObserverPrompt, OBSERVER_PROMPTS,
} from "./observer";

// ---- shape ---------------------------------------------------------------

export type CircleKind =
  | "passion" | "cause" | "hobby" | "dream" | "pull" | "background" | "signal"
  /** X-OBSERVER-001. Someone else noticed it; the learner confirmed it. */
  | "observation";

/** One question that put this circle on the page. */
export interface AuditInput {
  /** Item code where one exists, or the CANON-PENDING code we use today. */
  itemCode: string;
  /** Screen id, so it can be traced in the response log. */
  screen: string;
  /** The question, in the words the learner saw. */
  question: string;
  /** What they answered, in their words wherever possible. */
  answer: string;
}

export interface ConvergenceRole {
  ciId: string;
  title: string;
  family: string;
  /** The engine's fit score for this role, 0..100. Never recomputed here. */
  fit: number;
}

export interface ConvergenceCircle {
  id: string;
  label: string;
  kind: CircleKind;
  /** One line saying what this circle is and why it exists. */
  rationale: string;
  /** Career families this circle covers. Empty for Part 2 signal circles. */
  families: string[];
  /** The questions that produced it. */
  inputs: AuditInput[];
  /** Eligible roles inside it, strongest first. */
  roles: ConvergenceRole[];
}

/**
 * How much of a converged role rests on the learner's own words.
 *
 * This is a *provenance* label, not a probability. A role that only appears
 * because someone else noticed something is a genuinely weaker claim than one
 * the learner named twice themselves, and saying so is the honest version of
 * showing a second opinion at all.
 */
export type RoleConfidence = "corroborated" | "mixed" | "observation_only";

export const CONFIDENCE_LABEL: Record<RoleConfidence, string> = {
  corroborated: "You said it yourself, more than once",
  mixed: "Your words, widened by what they noticed",
  observation_only: "Only from what they noticed — worth a look, not a verdict",
};

export const CONFIDENCE_SHORT: Record<RoleConfidence, string> = {
  corroborated: "Your own words",
  mixed: "Yours + observed",
  observation_only: "Observed only",
};

export interface ConvergedRole extends ConvergenceRole {
  circleIds: string[];
  /** One line per circle saying why this role sits in that circle. */
  reasons: { circleId: string; circleLabel: string; why: string }[];
  /** Circles that came from the learner's own answers. */
  firstPersonCircles: number;
  /** Circles that came from an approved observation. */
  observerCircles: number;
  confidence: RoleConfidence;
}

export interface EligibilityAudit {
  note: string;
  inputs: AuditInput[];
  eligiblePool: number;
  ruledOutByStudy: number;
  studyRelaxed: boolean;
}

/**
 * What an approved observation actually did to this diagram — the receipt.
 *
 * Every number here is read back out of the same bounded channel the engine
 * used: `OBSERVER_ITEM_WEIGHT` per item, the whole lot scaled down to
 * `OBSERVER_TOTAL_CAP`, and nothing at all for a family the learner already
 * named. If the cap bit, we say so, because "we listened to them but only
 * this much" is the promise the feature is built on.
 */
export interface ObserverImpact {
  present: boolean;
  /** "your parent", "your teacher or coach", … */
  noun: string;
  observerName?: string;
  itemsApproved: number;
  /** Items the learner retracted or ticked away. Kept, so the receipt is whole. */
  itemsDeclined: number;
  itemWeight: number;
  totalCap: number;
  /** Affinity before the cap, and after it. */
  rawTotal: number;
  appliedTotal: number;
  capApplied: boolean;
  /** Families the observation opened up, with the strength it carried. */
  families: { family: string; strength: number }[];
  /** Families the observer named that the learner had already named, so they
   *  contributed nothing. Silence here would look like double-counting. */
  alreadyYours: string[];
  circleIds: string[];
  /** Roles these circles put on the page, with their provenance label. */
  roles: {
    ciId: string; title: string; family: string; fit: number;
    confidence: RoleConfidence; firstPersonCircles: number; observerCircles: number;
  }[];
  /** Approved free-text, and whether the learner reworded it. */
  notes: { id: ObserverPrompt["id"]; question: string; answer: string; edited: boolean }[];
  method: string;
}

export interface Convergence {
  circles: ConvergenceCircle[];
  /** Roles present in two or more circles, most-crossed first. */
  converged: ConvergedRole[];
  eligibility: EligibilityAudit;
  /** The "common purpose" line, when the circles genuinely share one. */
  purpose: string | null;
  /** How the diagram was built, said plainly. */
  method: string;
  /** X-OBSERVER-001 receipt. `present: false` when nobody was asked. */
  observer: ObserverImpact;
  empty: boolean;
}

// ---- thresholds ----------------------------------------------------------

/** Most circles a diagram can carry before it stops being readable. */
export const MAX_CIRCLES = 8;
/** Roles listed inside one circle. The diagram shows five, like the print. */
export const ROLES_PER_CIRCLE = 5;
/** A Part 2 signal circle needs this much drift from neutral 50 to exist. */
export const SIGNAL_DRIFT = 15;
/** Fit score at which a role counts as sitting inside a signal circle. */
export const SIGNAL_HIT = 62;
/** Converged roles listed in the report. */
export const MAX_CONVERGED = 10;

// ---- Part 2 signal circles ----------------------------------------------

const DOMAIN_CIRCLE: Record<ImpactDomain, { label: string; rationale: string }> = {
  care:    { label: "People & Care",      rationale: "you said the problem you most want to solve is people who are unwell or in need of care" },
  grow:    { label: "Building & Growth",  rationale: "you said the problem you most want to solve is people and businesses trying to build and grow" },
  planet:  { label: "Planet & Future",    rationale: "you said the problem you most want to solve is the planet and the future" },
  learn:   { label: "Learning & Meaning", rationale: "you said the problem you most want to solve is people who want to learn, or to be moved by something" },
  systems: { label: "Systems & Community", rationale: "you said the problem you most want to solve is systems and communities that need to work better" },
};

const DOMAIN_ANSWER: Record<ImpactDomain, string> = {
  care: "People who are unwell or in need of care",
  grow: "People and businesses trying to build and grow",
  planet: "The planet and the future",
  learn: "People who want to learn, or to be moved by something",
  systems: "Systems and communities that need to work better",
};

/** Questions we quote in the audit trail, keyed by screen id. */
const QUESTION: Record<string, string> = {
  px_passions: "What lights you up? (up to seven)",
  px_hobby_frequency: "How often do you actually do this?",
  px_cause_rank: "Which of these would you fix first?",
  px_cause_role: "If you worked on this, which would you most want to do?",
  px_childhood_dream: "When you were ten, what did you want to be?",
  px_career_pull: "Which of these caught your eye?",
  p1_background: "What field have you been working in, and what do you want next?",
  p2_whose_problem: "Whose problem would you most want to be the one to solve?",
  p2_creative_room: "How much room to invent do you want?",
  p2_people_battery: "How much people contact can you sustain?",
  p2_focus_slider: "Deep focus, or switching between things?",
  p1_qualification: "What have you finished studying?",
  p1_stream: "Which stream are you in?",
  p1_study_appetite: "How much more studying are you up for?",
  p1_salary_floor: "What's the least you could take home and still be OK?",
  p1_money_matters: "How much does money matter to you right now?",
  p1_experience_location: "How much paid experience do you have, and where do you work?",
  p1_study_mobility: "How far would you move to study?",
};

const q = (screen: string) => QUESTION[screen] ?? screen;

interface SignalSpec {
  id: string;
  slot: SlotKey;
  /** Where the per-role fit for this signal lives on a MatchResult. */
  read: (r: MatchResult) => number | undefined;
  /** Label + one-liner for the high and low ends. */
  high: { label: string; rationale: string };
  low: { label: string; rationale: string };
  /** Screens that can write this slot, for the audit trail. */
  screens: string[];
  itemCode: string;
}

/**
 * The three Part 2 signals worth drawing as their own circle. Each is already
 * a scored slot with per-role fit, so a circle adds no new construct — it just
 * shows the learner where that one answer took their list.
 */
const SIGNALS: SignalSpec[] = [
  {
    id: "signal-creative",
    slot: "prefCreativity",
    read: (r) => r.valuesDetail?.creativity,
    high: { label: "Room To Invent", rationale: "you chose a blank page over a clear brief, so these are roles that leave the shape of the work to you" },
    low: { label: "A Clear Brief", rationale: "you chose a clear brief over a blank page, so these are roles where the problem arrives already defined" },
    screens: ["p2_creative_room", "p2_scenario_open_week"],
    itemCode: "W-CREATE-001",
  },
  {
    id: "signal-people",
    slot: "tolPeopleContact",
    read: (r) => r.contextDetail?.people,
    high: { label: "People All Day", rationale: "you said sustained people contact is where your energy comes from, and these roles run on it" },
    low: { label: "Quiet & Uninterrupted", rationale: "you said constant people contact drains you, so these are roles that respect that" },
    screens: ["p2_people_battery", "p2_collapse", "p2_hard_day"],
    itemCode: "W-BATTERY-001",
  },
  {
    id: "signal-focus",
    slot: "prefFocusVsSwitching",
    read: (r) => r.valuesDetail?.focus,
    high: { label: "One Thing, Deeply", rationale: "you'd rather go deep on one problem than juggle several, and these roles allow that" },
    low: { label: "Many Threads", rationale: "you'd rather keep several things moving than sit with one, and these roles are built that way" },
    screens: ["p2_focus_slider"],
    itemCode: "W-FOCUS-001",
  },
];

// ---- helpers -------------------------------------------------------------

function rolesInFamilies(results: MatchResult[], families: string[]): ConvergenceRole[] {
  const set = new Set(families);
  return results
    .filter((r) => set.has(r.family))
    .sort((a, b) => b.score - a.score)
    .slice(0, ROLES_PER_CIRCLE)
    .map((r) => ({ ciId: r.ciId, title: r.title, family: r.family, fit: Math.round(r.score) }));
}

function rolesByFit(
  results: MatchResult[], read: (r: MatchResult) => number | undefined,
): ConvergenceRole[] {
  return results
    .map((r) => ({ r, v: read(r) ?? 0 }))
    .filter((x) => x.v >= SIGNAL_HIT)
    .sort((a, b) => b.v - a.v || b.r.score - a.r.score)
    .slice(0, ROLES_PER_CIRCLE)
    .map(({ r }) => ({ ciId: r.ciId, title: r.title, family: r.family, fit: Math.round(r.score) }));
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

const passionById = (id: string): Passion | undefined => PASSIONS.find((p) => p.id === id);

/**
 * Provenance label for a converged role.
 *
 * Two of the learner's own circles is the strong claim. One of theirs plus an
 * observation is a widened claim. No first-person circle at all means the role
 * is on the page purely because someone else noticed something — real, but it
 * must never be dressed up as self-knowledge.
 */
export function roleConfidence(r: {
  firstPersonCircles: number; observerCircles: number;
}): RoleConfidence {
  if (r.firstPersonCircles >= 2) return "corroborated";
  if (r.firstPersonCircles >= 1 && r.observerCircles >= 1) return "mixed";
  if (r.firstPersonCircles >= 1) return "corroborated";
  return "observation_only";
}

function slotAudit(
  slots: SlotState, slot: SlotKey, screens: string[], itemCode: string,
): AuditInput[] {
  const contribs = (slots.contribs[slot] ?? []).filter((c) => c.value != null);
  if (contribs.length > 0) {
    return contribs.map((c) => ({
      itemCode: c.screenId,
      screen: screens[0],
      question: q(screens[0]),
      answer: c.label ?? `You answered ${Math.round(c.value)} on 0–100`,
    }));
  }
  return [{
    itemCode,
    screen: screens[0],
    question: q(screens[0]),
    answer: `Read from your ${slot} answer`,
  }];
}

// ---- build ---------------------------------------------------------------

export interface ConvergenceInput {
  band?: Band;
  part1: Part1Answers;
  slots?: SlotState;
  learner: Learner;
  results: MatchResult[];
  eligiblePool: number;
  ruledOutByStudy: number;
  studyRelaxed?: boolean;
  /** X-OBSERVER-001. Approved-only; an unreviewed observation must not reach here. */
  observer?: ApprovedObserverInput;
  /** The learner's own family affinity, for the "adds nothing you already
   *  named" bound. Pass the same map the engine scored with. */
  firstPersonStrength?: Map<string, number>;
}

export function buildConvergence(input: ConvergenceInput): Convergence {
  const band = input.band ?? DEFAULT_BAND;
  const part1 = input.part1;
  const slots = input.slots ?? emptySlots();
  const results = input.results;
  const circles: ConvergenceCircle[] = [];

  // --- stated interests: passions, hobbies (with frequency), causes -------
  const freq = part1.hobby_frequency ?? {};
  for (const p of selectedPassions(part1.passion_ids)) {
    const hobby = isHobbyId(p.id);
    const cause = isCauseId(p.id);
    const rankIdx = part1.cause_rank_ids?.indexOf(p.id) ?? -1;
    const inputs: AuditInput[] = [{
      itemCode: "X-PASSION-001",
      screen: "px_passions",
      question: q("px_passions"),
      answer: p.label,
    }];
    if (hobby && freq[p.id]) {
      inputs.push({
        itemCode: "X-HOBBY-002",
        screen: "px_hobby_frequency",
        question: q("px_hobby_frequency"),
        answer: `You do it ${HOBBY_FREQUENCY_PHRASE[freq[p.id]]}`,
      });
    }
    if (cause && rankIdx >= 0) {
      inputs.push({
        itemCode: "X-CAUSE-001",
        screen: "px_cause_rank",
        question: q("px_cause_rank"),
        answer: `Your ${ORDINAL[rankIdx] ?? "ranked"} priority`,
      });
    }

    const rationale = hobby && freq[p.id]
      ? `you already do this ${HOBBY_FREQUENCY_PHRASE[freq[p.id]]}, and these are the roles that pay for that instinct`
      : cause && rankIdx >= 0
      ? `you ranked this your ${ORDINAL[rankIdx] ?? "top"} priority, and this is what working on it looks like as a job`
      : `you told us you're drawn to this`;

    circles.push({
      id: `passion-${p.id}`,
      label: p.label,
      kind: hobby ? "hobby" : cause ? "cause" : "passion",
      rationale,
      families: p.families,
      inputs,
      roles: rolesInFamilies(results, p.families),
    });
  }

  // Ranked causes the learner never tapped on the passions screen still
  // shape the list, so they still get a circle.
  (part1.cause_rank_ids ?? []).forEach((id, i) => {
    if (circles.some((c) => c.id === `passion-${id}`)) return;
    const cause = causeById(id);
    if (!cause) return;
    circles.push({
      id: `cause-${id}`,
      label: cause.label,
      kind: "cause",
      rationale: `you ranked this your ${ORDINAL[i] ?? "top"} priority, and this is what working on it looks like as a job`,
      families: cause.families,
      inputs: [{
        itemCode: "X-CAUSE-001",
        screen: "px_cause_rank",
        question: q("px_cause_rank"),
        answer: `${cause.label} — your ${ORDINAL[i] ?? "ranked"} priority`,
      }],
      roles: rolesInFamilies(results, cause.families),
    });
  });

  // --- childhood dream and career pulls ----------------------------------
  const dream = dreamById(part1.childhood_dream);
  if (dream) {
    circles.push({
      id: `dream-${dream.id}`,
      label: dream.label,
      kind: "dream",
      rationale: "what you wanted to be at ten, before anyone told you what was sensible — a clue, not a plan",
      families: dream.families,
      inputs: [{
        itemCode: "X-DREAM-001",
        screen: "px_childhood_dream",
        question: q("px_childhood_dream"),
        answer: dream.label,
      }],
      roles: rolesInFamilies(results, dream.families),
    });
  }
  for (const pull of selectedPulls(part1.career_pull_ids)) {
    circles.push({
      id: `pull-${pull.id}`,
      label: pull.label,
      kind: "pull",
      rationale: "work you told us catches your eye when you see someone doing it",
      families: pull.families,
      inputs: [{
        itemCode: "X-PULL-001",
        screen: "px_career_pull",
        question: q("px_career_pull"),
        answer: pull.label,
      }],
      roles: rolesInFamilies(results, pull.families),
    });
  }

  // --- professional background (18+, and only when they want to stay near it)
  if (band === "18plus" && (part1.career_intent === "deepen" || part1.career_intent === "adjacent")) {
    const field = fieldById(part1.professional_field);
    if (field) {
      circles.push({
        id: `background-${field.id}`,
        label: field.label,
        kind: "background",
        rationale: part1.career_intent === "adjacent"
          ? "the field you already work in — you said you'd move somewhere next to it, so what you've built still counts"
          : "the field you already work in, where your experience is worth the most",
        families: field.families,
        inputs: [{
          itemCode: "X-BACKGROUND-001",
          screen: "p1_background",
          question: q("p1_background"),
          answer: `${field.label} · ${part1.career_intent === "adjacent" ? "somewhere next to it" : "deepen where you are"}`,
        }],
        roles: rolesInFamilies(results, field.families),
      });
    }
  }

  // --- approved observations (X-OBSERVER-001) ----------------------------
  // Drawn as their own circles, never folded into the learner's. A reader has
  // to be able to see at a glance which rings are their own words and which
  // are someone else's, confirmed — that distinction is the consent made
  // visible.
  const observer = input.observer;
  const firstPerson = input.firstPersonStrength ?? new Map<string, number>();
  const observerStrength = observerFamilyCounts(observer, firstPerson);
  const observerCircleIds: string[] = [];
  const alreadyYours: string[] = [];

  if (observer) {
    const noun = relationshipNoun(observer.relationship);
    for (const id of [...observer.cause_ids, ...observer.passion_ids]) {
      const item = passionById(id);
      if (!item) continue;
      // Only the families that actually carried weight. A family the learner
      // already named got nothing from the observer, so it gets no ring here.
      const contributing = item.families.filter((f) => observerStrength.has(f));
      const suppressed = item.families.filter((f) => firstPerson.has(f));
      for (const f of suppressed) if (!alreadyYours.includes(f)) alreadyYours.push(f);
      if (contributing.length === 0) continue;
      const circleId = `observation-${id}`;
      if (circles.some((c) => c.id === circleId)) continue;
      observerCircleIds.push(circleId);
      circles.push({
        id: circleId,
        label: item.label,
        kind: "observation",
        rationale: `${noun} noticed this in you and you confirmed it — carried at half the weight of your own answers, and capped`,
        families: contributing,
        inputs: [{
          itemCode: "X-OBSERVER-001",
          screen: "second_opinion",
          question: `What ${noun} noticed, and you approved`,
          answer: item.label,
        }],
        roles: rolesInFamilies(results, contributing),
      });
    }
  }

  // --- Part 2 signal circles --------------------------------------------
  const d1 = input.learner.impactDomain1;
  if (d1) {
    const spec = DOMAIN_CIRCLE[d1];
    circles.push({
      id: `signal-domain-${d1}`,
      label: spec.label,
      kind: "signal",
      rationale: spec.rationale,
      families: [],
      inputs: [{
        itemCode: "W-PROBLEM-001",
        screen: "p2_whose_problem",
        question: q("p2_whose_problem"),
        answer: DOMAIN_ANSWER[d1],
      }],
      roles: rolesByFit(results, (r) => r.domain),
    });
  }

  for (const s of SIGNALS) {
    const value = slotValue(slots, s.slot);
    if (Math.abs(value - 50) < SIGNAL_DRIFT) continue; // no real signal, no circle
    const end = value >= 50 ? s.high : s.low;
    circles.push({
      id: s.id,
      label: end.label,
      kind: "signal",
      rationale: end.rationale,
      families: [],
      inputs: slotAudit(slots, s.slot, s.screens, s.itemCode),
      roles: rolesByFit(results, s.read),
    });
  }

  // --- trim -------------------------------------------------------------
  // Circles that contributed no eligible role are dropped from the diagram:
  // an empty ring reads as a failure of the learner rather than of our data.
  const drawn = circles.filter((c) => c.roles.length > 0).slice(0, MAX_CIRCLES);

  // --- the convergence --------------------------------------------------
  const byRole = new Map<string, ConvergedRole>();
  for (const c of drawn) {
    for (const role of c.roles) {
      const existing = byRole.get(role.ciId);
      const reason = {
        circleId: c.id,
        circleLabel: c.label,
        why: c.kind === "signal"
          ? `${c.label}: ${c.rationale}`
          : `${c.label}: ${c.rationale}`,
      };
      const observed = c.kind === "observation";
      if (existing) {
        existing.circleIds.push(c.id);
        existing.reasons.push(reason);
        if (observed) existing.observerCircles += 1;
        else existing.firstPersonCircles += 1;
      } else {
        byRole.set(role.ciId, {
          ...role,
          circleIds: [c.id],
          reasons: [reason],
          firstPersonCircles: observed ? 0 : 1,
          observerCircles: observed ? 1 : 0,
          confidence: "corroborated", // set once the crossings are known
        });
      }
    }
  }

  for (const r of byRole.values()) r.confidence = roleConfidence(r);

  const converged = [...byRole.values()]
    .filter((r) => r.circleIds.length >= 2)
    .sort((a, b) =>
      b.circleIds.length - a.circleIds.length ||
      b.fit - a.fit ||
      (a.title < b.title ? -1 : 1))
    .slice(0, MAX_CONVERGED);

  // --- eligibility audit -------------------------------------------------
  const eligibility: EligibilityAudit = {
    note: band === "12to14"
      ? "Nothing was filtered out. At your age we ask for no qualification, no salary floor and no work history, so every career in the corpus stayed in the pool."
      : "Only careers you can actually reach appear in these circles. That gate runs once, before the diagram, and no circle can pull a career back through it.",
    inputs: eligibilityInputs(band, part1, input.learner),
    eligiblePool: input.eligiblePool,
    ruledOutByStudy: input.ruledOutByStudy,
    studyRelaxed: input.studyRelaxed ?? false,
  };

  const drawnObserverIds = drawn.filter((c) => c.kind === "observation").map((c) => c.id);
  const rawTotal = observer
    ? [...observer.passion_ids, ...observer.cause_ids].reduce((sum, id) => {
        const item = passionById(id);
        if (!item) return sum;
        return sum + item.families.filter((f) => !firstPerson.has(f)).length * OBSERVER_ITEM_WEIGHT;
      }, 0)
    : 0;
  const appliedTotal = [...observerStrength.values()].reduce((a, b) => a + b, 0);

  const observerImpact: ObserverImpact = {
    present: Boolean(observer) && drawnObserverIds.length > 0,
    noun: relationshipNoun(observer?.relationship),
    observerName: observer?.observer_name,
    itemsApproved: observer ? observer.passion_ids.length + observer.cause_ids.length : 0,
    itemsDeclined: observer
      ? (observer.declined_passion_ids?.length ?? 0) + (observer.declined_cause_ids?.length ?? 0)
      : 0,
    itemWeight: OBSERVER_ITEM_WEIGHT,
    totalCap: OBSERVER_TOTAL_CAP,
    rawTotal: round2(rawTotal),
    appliedTotal: round2(appliedTotal),
    capApplied: rawTotal > OBSERVER_TOTAL_CAP + 1e-9,
    families: [...observerStrength.entries()]
      .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))
      .map(([family, strength]) => ({ family, strength: round2(strength) })),
    alreadyYours: [...alreadyYours].sort(),
    circleIds: drawnObserverIds,
    roles: [...byRole.values()]
      .filter((r) => r.circleIds.some((id) => drawnObserverIds.includes(id)))
      .sort((a, b) => b.fit - a.fit || (a.title < b.title ? -1 : 1))
      .map((r) => ({
        ciId: r.ciId, title: r.title, family: r.family, fit: r.fit,
        confidence: r.confidence,
        firstPersonCircles: r.firstPersonCircles,
        observerCircles: r.observerCircles,
      })),
    notes: observer
      ? OBSERVER_PROMPTS
          .filter((prompt) => (observer.edited_notes?.[prompt.id] ?? "").trim().length > 0)
          .map((prompt) => ({
            id: prompt.id,
            question: prompt.question,
            answer: (observer.edited_notes?.[prompt.id] ?? "").trim(),
            edited: (observer.edited_note_ids ?? []).includes(prompt.id),
          }))
      : [],
    method:
      `Each thing ${relationshipNoun(observer?.relationship)} noticed — and you approved — is worth ` +
      `${OBSERVER_ITEM_WEIGHT} of one of your own answers, and everything they said is capped at ` +
      `${OBSERVER_TOTAL_CAP} together. Where they named something you had already told us yourself, ` +
      `it added nothing at all: agreement must not count twice. No observation can rule a career in ` +
      `or out — it can only widen what you get to see.`,
  };

  return {
    circles: drawn,
    converged,
    eligibility,
    observer: observerImpact,
    purpose: purposeLine(drawn),
    method:
      "Each circle is one thing you told us or one choice we watched you make. A role sits inside a circle " +
      "when it belongs to that interest's career families, or when the engine scored it a strong fit on that " +
      "signal. Roles in two or more circles are your convergence — that's where your interests stop competing " +
      "and start compounding.",
    empty: drawn.length < 2,
  };
}

function eligibilityInputs(
  band: Band, part1: Part1Answers, L: Learner,
): AuditInput[] {
  if (band === "12to14") return [];
  const out: AuditInput[] = [];

  if (band === "15to18") {
    if (part1.stream) {
      out.push({
        itemCode: "X-QUAL-001", screen: "p1_stream", question: q("p1_stream"),
        answer: part1.stream_provisional ? `${part1.stream} (provisional — inferred, not chosen)` : part1.stream,
      });
    }
  } else if (part1.qualification) {
    out.push({
      itemCode: "X-QUAL-001", screen: "p1_qualification", question: q("p1_qualification"),
      answer: part1.qualification,
    });
  }

  if (part1.study_appetite) {
    out.push({
      itemCode: "X-STUDY-001", screen: "p1_study_appetite", question: q("p1_study_appetite"),
      answer: `${part1.study_appetite} → we treated that as "${L.studyCapacity}" study capacity`,
    });
  }

  if (band === "18plus") {
    out.push({
      itemCode: "X-PAY-001", screen: "p1_salary_floor", question: q("p1_salary_floor"),
      answer: L.salaryFloorLpa > 0
        ? `A floor of about ₹${L.salaryFloorLpa} LPA — applied as a penalty, never a deletion`
        : "No floor given, so nothing was ruled out on pay",
    });
    out.push({
      itemCode: "X-GEO-001", screen: "p1_experience_location", question: q("p1_experience_location"),
      answer: `${L.yearsExperience ?? 0} years of paid experience${(L.yearsExperience ?? 0) >= 3 ? " — which raised your reach by a tier" : ""}`,
    });
  } else if (part1.money_matters) {
    out.push({
      itemCode: "X-PAY-001", screen: "p1_money_matters", question: q("p1_money_matters"),
      answer: `${part1.money_matters} — relative only, no salary floor is applied at your age`,
    });
  }

  if (part1.study_mobility) {
    out.push({
      itemCode: "X-MOBILITY-001", screen: "p1_study_mobility", question: q("p1_study_mobility"),
      answer: `${part1.study_mobility} — a bounded adjustment, never a filter`,
    });
  }

  return out;
}

/** The "common purpose" line, only when the circles genuinely share one. */
function purposeLine(circles: ConvergenceCircle[]): string | null {
  if (circles.length < 3) return null;
  const families = new Map<string, number>();
  for (const c of circles) for (const f of c.families) {
    families.set(f, (families.get(f) ?? 0) + 1);
  }
  const shared = [...families.entries()]
    .filter(([, n]) => n >= 2)
    .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))
    .slice(0, 2)
    .map(([f]) => f);
  if (shared.length === 0) return null;
  return shared.length === 1
    ? `Everything you told us keeps pointing back at the same place: ${shared[0]}.`
    : `Your interests keep meeting in two places: ${shared[0]} and ${shared[1]}.`;
}

// ---- text rendering for the downloadable report -------------------------

/**
 * The convergence as plain text. Used by the report so the intersection
 * logic survives print, PDF and a screen reader reading top to bottom —
 * three places a diagram alone says nothing.
 */
export function convergenceText(c: Convergence): string {
  const lines: string[] = [];
  lines.push("CONVERGENCE OF PASSIONS & CAREERS");
  lines.push("");
  lines.push(c.method);
  if (c.purpose) { lines.push(""); lines.push(c.purpose); }

  lines.push("");
  lines.push(`YOUR CIRCLES (${c.circles.length})`);
  for (const circle of c.circles) {
    lines.push("");
    lines.push(`• ${circle.label} — ${circle.rationale}`);
    if (circle.families.length > 0) {
      lines.push(`  Career families: ${circle.families.join(", ")}`);
    } else {
      lines.push("  Drawn from a Part 2 signal, so it spans families rather than sitting in one.");
    }
    lines.push(`  Roles: ${circle.roles.map((r) => `${r.title} (${r.fit})`).join(", ")}`);
    for (const i of circle.inputs) {
      lines.push(`  Audit · ${i.itemCode} · ${i.question} → ${i.answer}`);
    }
  }

  lines.push("");
  lines.push("WHERE THEY CONVERGE");
  if (c.converged.length === 0) {
    lines.push("No role sits in two of your circles yet. That is a real answer, not a gap: your interests currently point at different kinds of work, and you can follow more than one.");
  } else {
    for (const r of c.converged) {
      lines.push("");
      lines.push(`${r.title} — ${r.family} · fit ${r.fit} · sits in ${r.circleIds.length} of your circles`);
      lines.push(`  Where this comes from: ${CONFIDENCE_LABEL[r.confidence]} (${r.firstPersonCircles} from you, ${r.observerCircles} from what was noticed about you)`);
      for (const why of r.reasons) lines.push(`  – ${why.why}`);
    }
  }

  if (c.observer.present) {
    const o = c.observer;
    lines.push("");
    lines.push("WHAT SOMEONE WHO KNOWS YOU ADDED");
    lines.push(o.method);
    lines.push("");
    lines.push(`Asked: ${o.observerName ?? o.noun} (${o.noun})`);
    lines.push(`You approved ${o.itemsApproved} of what they said${o.itemsDeclined > 0 ? ` and set aside ${o.itemsDeclined}` : ""}.`);
    lines.push(`Weight it carried: ${o.appliedTotal} of a possible ${o.totalCap}${o.capApplied ? ` — they said enough to be worth ${o.rawTotal}, so the cap held it down to ${o.appliedTotal}` : ""}.`);
    if (o.alreadyYours.length > 0) {
      lines.push(`Added nothing, because you had already told us yourself: ${o.alreadyYours.join(", ")}.`);
    }
    if (o.families.length > 0) {
      lines.push(`Widened your view of: ${o.families.map((f) => `${f.family} (+${f.strength})`).join(", ")}.`);
    }
    for (const n of o.notes) {
      lines.push(`  In their words · ${n.question} → ${n.answer}${n.edited ? " (you reworded this before approving it)" : ""}`);
    }
    if (o.roles.length > 0) {
      lines.push("");
      lines.push("Roles this put in front of you:");
      for (const r of o.roles) {
        lines.push(`  ${r.title} — ${r.family} · fit ${r.fit} · ${CONFIDENCE_LABEL[r.confidence]}`);
      }
    }
  }

  lines.push("");
  lines.push("WHAT WAS RULED IN OR OUT BEFORE THE DIAGRAM");
  lines.push(c.eligibility.note);
  lines.push(`Eligible careers considered: ${c.eligibility.eligiblePool.toLocaleString()}${c.eligibility.ruledOutByStudy > 0 ? ` · set aside for study reach: ${c.eligibility.ruledOutByStudy.toLocaleString()}` : ""}${c.eligibility.studyRelaxed ? " · your study limit was widened by one step so the list was not empty" : ""}`);
  for (const i of c.eligibility.inputs) {
    lines.push(`Audit · ${i.itemCode} · ${i.question} → ${i.answer}`);
  }

  return lines.join("\n");
}
